import {useEffect, useLayoutEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {NOTIFICATION_VARIANTS} from '@nur-portal/ui-kit';

import {showNotification} from '../components/notification-center/helpers';
import {dispatchArticleReactionsFetchEvent} from '../helpers/reactions';
import {articleReactionsHttpRepository} from '../repositories/article-reactions-http-repository';
import {articleReactionMemoryRepository} from '../repositories/article-reactions-memory-repositiory';
import {readArticlReactions} from '../use-cases/reactions/read-article-reactions';
import {readArticleUserREactions} from '../use-cases/reactions/read-article-user-reactions';

import {useUser} from './use-user';

const useArticleReactions = (articleId, shouldFetch = true) => {
  const [allReactions, setAllReactions] = useState(articleReactionMemoryRepository.getAllReactions());
  const [isLoading, setIsLoading] = useState(false);
  const user = useUser();
  const currentReaction = allReactions.user[articleId];
  const {t} = useTranslation();

  articleReactionMemoryRepository.subscribe((_, value) => setAllReactions(value));

  useLayoutEffect(() => {
    if (shouldFetch) {
      articleReactionMemoryRepository.initAllReactions(articleId);
    }
  }, [shouldFetch]);

  useEffect(() => {
    readArticlReactions(articleId).then(() => {
      if (user.isAuthenticated && shouldFetch) {
        readArticleUserREactions(articleId).then(dispatchArticleReactionsFetchEvent);
      }
    });
  }, [user.isAuthenticated, shouldFetch]);

  const onSetUserReaction = reactionType => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    if (reactionType === allReactions.user[articleId]) {
      articleReactionsHttpRepository.deleteUserReaction(articleId).then(data => {
        if (!data?.tokenError) {
          articleReactionMemoryRepository.deleteUserReaction(articleId);
        }
        setIsLoading(false);
      }
      );
      return;
    }

    articleReactionsHttpRepository.setUserReaction(articleId, reactionType).then(data => {
      if (!data.ok) {
        showNotification({
          variant: NOTIFICATION_VARIANTS.ALERT,
          text: t(`articleReactions.error.${data.status === 429 ? data.status : 'other'}`)
        });
        setIsLoading(false);

        return;
      }
      if (!data?.tokenError) {
        articleReactionMemoryRepository.addReaction(articleId, reactionType);
        articleReactionMemoryRepository.setUserReaction(articleId, reactionType);
        setIsLoading(false);
      }
    }
    );
  };

  return {
    allReactions: allReactions.all[articleId],
    userReaction: currentReaction,
    onSetUserReaction
  };
};

export {useArticleReactions};
